// API Documentation Data for LiteralJS v8.1.11

// Category-based ASCII letter icons for method cards
const categoryIcons = {
	core: 'C',
	state: 'S',
	lifecycle: 'L',
	router: 'R'
};

export const apiData = [
	{
		id: 'component',
		name: 'component()',
		category: 'core',
		tags: ['factory', 'create', 'define', 'component'],
		description: 'Factory function that creates reusable component definitions. Returns a constructor function that can be instantiated by LiteralJS.',
		syntax: `component({
  name: 'MyComponent',
  state: { count: 0 },
  methods() {
    return {
      increment() {
        this.setState({ count: this.getState().count + 1 });
      }
    };
  },
  mounted() { /* ... */ },
  unmounted() { /* ... */ },
  render() {
    const { count } = this.getState();
    return <div>{count}</div>;
  }
})`,
		parameters: [
			{ name: 'name', type: 'string', description: 'Unique component name for debugging' },
			{ name: 'state', type: 'object', description: 'Initial state object (shallow merged on updates)' },
			{ name: 'methods', type: 'function', description: 'Returns methods object bound to component instance' },
			{ name: 'mounted', type: 'function', description: 'Called after component mounts to DOM' },
			{ name: 'unmounted', type: 'function', description: 'Called before component is removed from DOM' },
			{ name: 'render', type: 'function', description: 'Returns VNode tree (JSX or h() calls)' }
		],
		returns: { type: 'function', description: 'Component constructor' },
		example: `const Counter = component({
  name: 'Counter',
  state: { count: 0 },
  methods() {
    return {
      increment() {
        this.setState({ count: this.getState().count + 1 });
      }
    };
  },
  render() {
    const { count } = this.getState();
    return (
      <button events={{ click: () => this.increment() }}>
        Count: {count}
      </button>
    );
  }
});`,
		playgroundTemplate: `const MyComponent = component({
  name: 'MyComponent',
  state: { message: 'Hello LiteralJS!' },
  methods() {
    return {
      updateMessage() {
        this.setState({ message: 'You clicked me! ✨' });
      }
    };
  },
  render() {
    const { message } = this.getState();
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>{message}</h2>
        <button 
          style={{ 
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #cba6f7, #f5c2e7)',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
          events={{ click: () => this.updateMessage() }}
        >
          Click me!
        </button>
      </div>
    );
  }
});`
	},
	{
		id: 'app',
		name: 'App',
		category: 'core',
		tags: ['mount', 'container', 'app', 'instance'],
		description: 'Application container that manages state, events, and component lifecycle. Each App is isolated with its own state tree.',
		syntax: `const app = new App(RootComponent, store);
app.mount('root');`,
		parameters: [
			{ name: 'RootComponent', type: 'function', description: 'Root component created by component()' },
			{ name: 'store', type: 'object', optional: true, description: 'Initial app-level store state' }
		],
		methods: [
			{ name: 'mount(elementId)', description: 'Mount app to DOM element' },
			{ name: 'unmount()', description: 'Unmount app and clean up' },
			{ name: 'scheduleUpdate()', description: 'Trigger a re-render manually' }
		],
		example: `const store = { user: null, theme: 'dark' };
const app = new App(AppComponent, store);
app.mount('root');

// Access store from any component
const { theme } = this.getStore();
this.setStore({ theme: 'light' });`,
		playgroundTemplate: `const Root = component({
  name: 'Root',
  state: { clicks: 0 },
  methods() {
    return {
      handleClick() {
        this.setState({ clicks: this.getState().clicks + 1 });
      }
    };
  },
  render() {
    const { clicks } = this.getState();
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>App Container Demo</h1>
        <p>Clicks: {clicks}</p>
        <button 
          style={{ padding: '0.5rem 1rem', fontSize: '1rem' }}
          events={{ click: () => this.handleClick() }}
        >
          Click me!
        </button>
      </div>
    );
  }
});

const app = new App(Root);
app.mount('demo-root');`
	},
	{
		id: 'h',
		name: 'h()',
		category: 'core',
		tags: ['hyperscript', 'jsx', 'vnode', 'create'],
		description: 'Hyperscript function that creates virtual DOM nodes (VNodes). Used as JSX pragma or called directly.',
		syntax: `h(type, props, ...children)`,
		parameters: [
			{ name: 'type', type: 'string|function', description: 'HTML tag name or component function' },
			{ name: 'props', type: 'object', description: 'Attributes, events, and ref' },
			{ name: 'children', type: 'any', description: 'Text, VNodes, or arrays' }
		],
		returns: { type: 'VNode', description: 'Virtual DOM node' },
		example: `// JSX (with /** @jsx h */ pragma)
const element = <div class="hello">World</div>;

// Equivalent h() call
const element = h('div', { class: 'hello' }, 'World');

// With events
const button = h('button', {
  events: { click: () => console.log('clicked') }
}, 'Click me');`,
		playgroundTemplate: `const Demo = component({
  name: 'Demo',
  render() {
    // You can use JSX or h() directly
    return h('div', { 
      style: { padding: '2rem', textAlign: 'center' } 
    }, [
      h('h2', {}, 'h() Function Demo'),
      h('p', {}, 'This was created with h() instead of JSX!'),
      h('button', {
        style: {
          padding: '0.75rem 1.5rem',
          background: '#89dceb',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer'
        },
        events: {
          click: () => alert('h() works!')
        }
      }, 'Click me')
    ]);
  }
});`
	},
	{
		id: 'getstate',
		name: 'getState()',
		category: 'state',
		tags: ['state', 'get', 'read'],
		description: 'Returns current component state. Use destructuring to access specific properties.',
		syntax: `const { count, name } = this.getState();`,
		returns: { type: 'object', description: 'Current component state' },
		example: `component({
  name: 'UserCard',
  state: { name: 'Alice', age: 30 },
  render() {
    const { name, age } = this.getState();
    return (
      <div>
        <h3>{name}</h3>
        <p>Age: {age}</p>
      </div>
    );
  }
});`,
		playgroundTemplate: `const StateDemo = component({
  name: 'StateDemo',
  state: { 
    username: 'LiteralJSFan',
    level: 42,
    online: true 
  },
  render() {
    const { username, level, online } = this.getState();
    return (
      <div style={{ padding: '2rem' }}>
        <h3>User Profile</h3>
        <p>Username: {username}</p>
        <p>Level: {level}</p>
        <p>Status: {online ? '🟢 Online' : '🔴 Offline'}</p>
      </div>
    );
  }
});`
	},
	{
		id: 'setstate',
		name: 'setState()',
		category: 'state',
		tags: ['state', 'set', 'update', 'merge'],
		description: 'Updates component state with shallow merge. Supports functional updates and callbacks.',
		syntax: `this.setState({ count: 1 });
this.setState(s => ({ count: s.count + 1 }));
this.setState({ count: 0 });`,
		parameters: [
			{ name: 'update', type: 'object|function', description: 'New state values or updater function' },
			{ name: 'callback', type: 'function', optional: true, description: 'Called after re-render completes' }
		],
		example: `component({
  name: 'Counter',
  state: { count: 0 },
  methods() {
    return {
      increment() {
        // Functional update (recommended for derived state)
        this.setState(s => ({ count: s.count + 1 }));
      },
      reset() {
        this.setState({ count: 0 });
      }
    };
  },
  render() {
    const { count } = this.getState();
    return (
      <div>
        <span>{count}</span>
        <button events={{ click: () => this.increment() }}>+</button>
        <button events={{ click: () => this.reset() }}>Reset</button>
      </div>
    );
  }
});`,
		playgroundTemplate: `const Counter = component({
  name: 'Counter',
  state: { count: 0 },
  methods() {
    return {
      increment() {
        this.setState(s => ({ count: s.count + 1 }));
      },
      decrement() {
        this.setState(s => ({ count: s.count - 1 }));
      },
      reset() {
        this.setState({ count: 0 });
      }
    };
  },
  render() {
    const { count } = this.getState();
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Count: {count}</h2>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
          <button events={{ click: () => this.decrement() }}>-</button>
          <button events={{ click: () => this.reset() }}>Reset</button>
          <button events={{ click: () => this.increment() }}>+</button>
        </div>
      </div>
    );
  }
});`
	},
	{
		id: 'getstore',
		name: 'getStore()',
		category: 'state',
		tags: ['store', 'app', 'global', 'shared'],
		description: 'Access app-level store shared across all components in the same App instance.',
		syntax: `const { user, theme } = this.getStore();`,
		returns: { type: 'object', description: 'App store state' },
		example: `// In your app initialization
const store = { theme: 'dark', user: null };
const app = new App(Root, store);

// In any component
const { theme } = this.getStore();
// theme === 'dark'`,
		playgroundTemplate: `const ThemeDisplay = component({
  name: 'ThemeDisplay',
  render() {
    const { theme } = this.getStore();
    return (
      <div style={{ 
        padding: '2rem',
        background: theme === 'dark' ? '#1e1e2e' : '#eff1f5',
        color: theme === 'dark' ? '#cdd6f4' : '#4c4f69',
        borderRadius: '12px'
      }}>
        <h3>Current Theme: {theme}</h3>
        <p>This reads from the app store!</p>
      </div>
    );
  }
});

const App = component({
  name: 'App',
  render() {
    return (
      <div>
        <ThemeDisplay />
        <ThemeDisplay />
      </div>
    );
  }
});

const app = new App(App, { theme: 'dark' });
app.mount('demo-root');`
	},
	{
		id: 'setstore',
		name: 'setStore()',
		category: 'state',
		tags: ['store', 'app', 'global', 'shared', 'update'],
		description: 'Update app-level store. Triggers re-render in all components that access the store.',
		syntax: `this.setStore({ theme: 'light' });`,
		parameters: [
			{ name: 'update', type: 'object', description: 'New store values (shallow merge)' }
		],
		example: `component({
  name: 'ThemeToggle',
  methods() {
    return {
      toggle() {
        const { theme } = this.getStore();
        this.setStore({ theme: theme === 'dark' ? 'light' : 'dark' });
      }
    };
  },
  render() {
    const { theme } = this.getStore();
    return (
      <button events={{ click: () => this.toggle() }}>
        Switch to {theme === 'dark' ? 'light' : 'dark'} mode
      </button>
    );
  }
});`,
		playgroundTemplate: `const ThemeToggle = component({
  name: 'ThemeToggle',
  methods() {
    return {
      toggleTheme() {
        const { theme } = this.getStore();
        this.setStore({ theme: theme === 'dark' ? 'light' : 'dark' });
      }
    };
  },
  render() {
    const { theme } = this.getStore();
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h3>Theme: {theme}</h3>
        <button 
          style={{
            padding: '0.75rem 1.5rem',
            background: theme === 'dark' ? '#cba6f7' : '#89dceb',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
          events={{ click: () => this.toggleTheme() }}
        >
          Toggle Theme
        </button>
      </div>
    );
  }
});

const App = component({
  name: 'App',
  render() {
    return (
      <div>
        <ThemeToggle />
        <ThemeToggle />
      </div>
    );
  }
});

const app = new App(App, { theme: 'dark' });
app.mount('demo-root');`
	},
	{
		id: 'mounted',
		name: 'mounted()',
		category: 'lifecycle',
		tags: ['lifecycle', 'mount', 'init', 'setup'],
		description: 'Lifecycle hook called after component is mounted to the DOM. Perfect for initialization, API calls, and setting up listeners.',
		syntax: `component({
  mounted() {
    // DOM is ready!
    this._timer = setInterval(() => this.tick(), 1000);
  }
});`,
		example: `component({
  name: 'Timer',
  state: { seconds: 0 },
  methods() {
    return {
      tick() {
        this.setState({ seconds: this.getState().seconds + 1 });
      }
    };
  },
  mounted() {
    // Start timer when component mounts
    this._timer = setInterval(() => this.tick(), 1000);
  },
  unmounted() {
    // Clean up to prevent memory leaks
    clearInterval(this._timer);
  },
  render() {
    const { seconds } = this.getState();
    return <div>Elapsed: {seconds}s</div>;
  }
});`,
		playgroundTemplate: `const Timer = component({
  name: 'Timer',
  state: { seconds: 0 },
  methods() {
    return {
      tick() {
        this.setState(s => ({ seconds: s.seconds + 1 }));
      }
    };
  },
  mounted() {
    this._timer = setInterval(() => this.tick(), 1000);
  },
  unmounted() {
    clearInterval(this._timer);
  },
  render() {
    const { seconds } = this.getState();
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>⏱️ Timer</h2>
        <p style={{ fontSize: '2rem' }}>{seconds}s</p>
        <p style={{ fontSize: '0.875rem', color: '#6c7086' }}>
          Started when mounted!
        </p>
      </div>
    );
  }
});`
	},
	{
		id: 'unmounted',
		name: 'unmounted()',
		category: 'lifecycle',
		tags: ['lifecycle', 'unmount', 'cleanup', 'destroy'],
		description: 'Lifecycle hook called before component is removed from DOM. Essential for cleanup to prevent memory leaks.',
		syntax: `component({
  unmounted() {
    // Clean up timers, listeners, subscriptions
    clearInterval(this._timer);
    window.removeEventListener('scroll', this._handler);
  }
});`,
		example: `component({
  name: 'ScrollTracker',
  state: { scrollY: 0 },
  methods() {
    return {
      handleScroll() {
        this.setState({ scrollY: window.scrollY });
      }
    };
  },
  mounted() {
    this._handler = () => this.handleScroll();
    window.addEventListener('scroll', this._handler);
  },
  unmounted() {
    // Always clean up listeners!
    window.removeEventListener('scroll', this._handler);
  },
  render() {
    const { scrollY } = this.getState();
    return <div>Scroll: {scrollY}px</div>;
  }
});`,
		playgroundTemplate: `const LifecycleDemo = component({
  name: 'LifecycleDemo',
  state: { mounted: false, mountCount: 0 },
  methods() {
    return {
      toggle() {
        this.setState({ mounted: !this.getState().mounted });
      }
    };
  },
  mounted() {
    this.setState({ mountCount: this.getState().mountCount + 1 });
  },
  render() {
    const { mounted, mountCount } = this.getState();
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <button events={{ click: () => this.toggle() }}>
          {mounted ? 'Unmount Child' : 'Mount Child'}
        </button>
        {mounted && <ChildComponent />}
      </div>
    );
  }
});

const ChildComponent = component({
  name: 'Child',
  state: { count: 0 },
  mounted() {
    this.setState({ count: this.getState().count + 1 });
  },
  unmounted() {
    this.setState({ count: 0 });
  },
  render() {
    return (
      <div style={{ 
        marginTop: '1rem',
        padding: '1rem',
        background: '#a6e3a1',
        borderRadius: '8px'
      }}>
        I'm alive! Check console for lifecycle logs.
      </div>
    );
  }
});`
	},
	{
		id: 'events',
		name: 'events prop',
		category: 'core',
		tags: ['events', 'click', 'input', 'delegate', 'weakmap'],
		description: 'Event handling using WeakMap delegation. Events are efficiently managed and automatically cleaned up on unmount.',
		syntax: `<button events={{ click: () => this.handleClick() }}>Click</button>`,
		parameters: [
			{ name: 'eventType', type: 'string', description: 'DOM event name (click, input, mouseenter, etc.)' },
			{ name: 'handler', type: 'function', description: 'Event handler function' }
		],
		example: `component({
  name: 'Form',
  state: { name: '', submitted: false },
  methods() {
    return {
      updateName(e) {
        this.setState({ name: e.target.value });
      },
      submit(e) {
        e.preventDefault();
        this.setState({ submitted: true });
      }
    };
  },
  render() {
    const { name, submitted } = this.getState();
    return (
      <form events={{ submit: (e) => this.submit(e) }}>
        <input
          value={name}
          events={{ input: (e) => this.updateName(e) }}
        />
        <button type="submit">Submit</button>
        {submitted && <p>Hello, {name}!</p>}
      </form>
    );
  }
});`,
		playgroundTemplate: `const InteractiveButton = component({
  name: 'InteractiveButton',
  state: { clicks: 0, hover: false },
  methods() {
    return {
      handleClick() {
        this.setState(s => ({ clicks: s.clicks + 1 }));
      },
      setHover(v) {
        this.setState({ hover: v });
      }
    };
  },
  render() {
    const { clicks, hover } = this.getState();
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <button
          style={{
            padding: '1rem 2rem',
            fontSize: '1.25rem',
            background: hover ? '#f5c2e7' : '#cba6f7',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            transform: hover ? 'scale(1.05)' : 'scale(1)',
            transition: 'all 0.2s'
          }}
          events={{
            click: () => this.handleClick(),
            mouseenter: () => this.setHover(true),
            mouseleave: () => this.setHover(false)
          }}
        >
          {hover ? '✨ Click me!' : 'Click me!'}
        </button>
        <p style={{ marginTop: '1rem' }}>
          Clicks: {clicks}
        </p>
      </div>
    );
  }
});`
	},
	{
		id: 'render',
		name: 'render()',
		category: 'core',
		tags: ['render', 'jsx', 'view', 'template'],
		description: 'Returns the virtual DOM tree for the component. Called on initial mount and after every state update.',
		syntax: `render() {
  const { count } = this.getState();
  return <div>{count}</div>;
}`,
		returns: { type: 'VNode', description: 'Virtual DOM node tree' },
		example: `component({
  name: 'UserList',
  state: { users: ['Alice', 'Bob', 'Carol'] },
  render() {
    const { users } = this.getState();
    return (
      <ul>
        {users.map(user => (
          <li key={user}>{user}</li>
        ))}
      </ul>
    );
  }
});`,
		playgroundTemplate: `const ListRender = component({
  name: 'ListRender',
  state: { 
    items: [
      { id: 1, text: 'Learn LiteralJS', done: true },
      { id: 2, text: 'Build something cool', done: false },
      { id: 3, text: 'Share with friends', done: false }
    ]
  },
  methods() {
    return {
      toggle(id) {
        this.setState({
          items: this.getState().items.map(item =>
            item.id === id ? { ...item, done: !item.done } : item
          )
        });
      }
    };
  },
  render() {
    const { items } = this.getState();
    return (
      <div style={{ padding: '2rem' }}>
        <h3>My Tasks</h3>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {items.map(item => (
            <li 
              key={item.id}
              style={{
                padding: '0.5rem',
                textDecoration: item.done ? 'line-through' : 'none',
                opacity: item.done ? 0.6 : 1,
                cursor: 'pointer'
              }}
              events={{ click: () => this.toggle(item.id) }}
            >
              {item.done ? '✅' : '⭕'} {item.text}
            </li>
          ))}
        </ul>
      </div>
    );
  }
});`
	},
	{
		id: 'router',
		name: 'Router()',
		category: 'router',
		tags: ['router', 'routing', 'navigation', 'spa'],
		description: 'Client-side router for single-page applications. Matches current URL against route definitions and renders the appropriate component.',
		syntax: `const routes = [
  { path: '/', render: () => h(HomePage) },
  { path: '/about', render: () => h(AboutPage) },
  { path: '/user/:id', render: (params) => h(UserPage, params) }
];

Router({ routes })`,
		parameters: [
			{ name: 'routes', type: 'array', description: 'Array of route objects with path and render properties' }
		],
		example: `import { Router, navigate } from 'literaljs-router';

const routes = [
  { path: '/', render: () => h(Home) },
  { path: '/about', render: () => h(About) },
  { path: '/user/:id', render: ({ id }) => h(UserProfile, { userId: id }) }
];

// In your app render
const App = component({
  render() {
    return (
      <div>
        <nav>
          <a href="/" events={{ click: () => navigate('/') }}>Home</a>
          <a href="/about" events={{ click: () => navigate('/about') }}>About</a>
        </nav>
        <main>{Router({ routes })}</main>
      </div>
    );
  }
});`,
		playgroundTemplate: `import { Router, navigate } from 'literaljs-router';

const Home = component({
  name: 'Home',
  render() {
    return <div style={{ padding: '2rem' }}><h2>🏠 Home</h2></div>;
  }
});

const About = component({
  name: 'About',
  render() {
    return <div style={{ padding: '2rem' }}><h2>ℹ️ About</h2></div>;
  }
});

const routes = [
  { path: '/', render: () => h(Home) },
  { path: '/about', render: () => h(About) }
];

const App = component({
  name: 'RouterApp',
  render() {
    return (
      <div>
        <nav style={{ padding: '1rem', borderBottom: '1px solid #ccc' }}>
          <button 
            style={{ marginRight: '0.5rem' }}
            events={{ click: () => navigate('/') }}
          >
            Home
          </button>
          <button events={{ click: () => navigate('/about') }}>
            About
          </button>
        </nav>
        <main>{Router({ routes })}</main>
      </div>
    );
  }
});

const app = new App(App);
app.mount('demo-root');`
	},
	{
		id: 'navigate',
		name: 'navigate()',
		category: 'router',
		tags: ['router', 'navigation', 'spa', 'programmatic'],
		description: 'Programmatically navigate to a route. Updates browser history and triggers a re-render.',
		syntax: `navigate('/path');
navigate('/user/123');`,
		parameters: [
			{ name: 'path', type: 'string', description: 'Target path to navigate to' }
		],
		example: `component({
  name: 'NavButton',
  methods() {
    return {
      goToProfile() {
        navigate('/profile');
      }
    };
  },
  render() {
    return (
      <button events={{ click: () => this.goToProfile() }}>
        View Profile
      </button>
    );
  }
});`,
		playgroundTemplate: `import { navigate } from 'literaljs-router';

const NavigationDemo = component({
  name: 'NavigationDemo',
  state: { currentPath: window.location.pathname },
  methods() {
    return {
      go(path) {
        navigate(path);
        this.setState({ currentPath: path });
      }
    };
  },
  render() {
    const { currentPath } = this.getState();
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h3>Navigation Demo</h3>
        <p>Current path: <code>{currentPath}</code></p>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
          <button events={{ click: () => this.go('/') }}>Go Home</button>
          <button events={{ click: () => this.go('/about') }}>Go About</button>
          <button events={{ click: () => this.go('/contact') }}>Go Contact</button>
        </div>
      </div>
    );
  }
});`
	},
	{
		id: 'autokeys',
		name: 'Auto-Keyed Siblings',
		category: 'core',
		tags: ['key', 'siblings', 'v8', 'reconciliation'],
		description: 'v8.0.5 feature: sibling components are automatically keyed by their render position, ensuring state isolation without manual key props.',
		syntax: `// v8.0.5 automatically assigns keys:
<div>
  <Counter />  {/* key: __auto_0__ */}
  <Counter />  {/* key: __auto_1__ */}
</div>`,
		example: `// Before v8.0.5: Had to manually key siblings
<div>
  <Counter key="a" />
  <Counter key="b" />
</div>

// v8.0.5: Auto-keyed by position
<div>
  <Counter />  {/* Each gets unique auto-key */}
  <Counter />
</div>`,
		playgroundTemplate: `const Counter = component({
  name: 'AutoKeyCounter',
  state: { count: 0 },
  methods() {
    return {
      increment() {
        this.setState(s => ({ count: s.count + 1 }));
      }
    };
  },
  render() {
    const { count } = this.getState();
    return (
      <div style={{ 
        padding: '1rem',
        margin: '0.5rem',
        background: '#cba6f7',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <p>Count: {count}</p>
        <button events={{ click: () => this.increment() }}>+</button>
      </div>
    );
  }
});

const AutoKeyDemo = component({
  name: 'AutoKeyDemo',
  render() {
    return (
      <div style={{ padding: '2rem' }}>
        <h3>Auto-Keyed Siblings</h3>
        <p>Each counter maintains its own state!</p>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Counter />
          <Counter />
          <Counter />
        </div>
      </div>
    );
  }
});`,
		relatedMethods: ['render', 'component', 'h'],
		commonPatterns: [
			{
				name: 'Dynamic Lists',
				snippet: `{items.map(item => (
  <ListItem key={item.id} data={item} />
))}`
			},
			{
				name: 'Sibling Components',
				snippet: `<Header />
<Sidebar />
<MainContent />`
			}
		]
	},
	{
		id: 'props',
		name: 'props',
		category: 'core',
		tags: ['props', 'properties', 'attributes', 'pass', 'data'],
		description: 'Pass data from parent to child components via props. Access with this.props inside child components.',
		syntax: `component({
  name: 'Child',
  props: ['title', 'count'],
  render() {
    const { title, count } = this.props;
    return <div>{title}: {count}</div>;
  }
});`,
		parameters: [
			{ name: 'props', type: 'array', description: 'Array of prop names to accept' }
		],
		example: `const UserCard = component({
  name: 'UserCard',
  props: ['name', 'role', 'avatar'],
  render() {
    const { name, role, avatar } = this.props;
    return (
      <div class="user-card">
        <img src={avatar} alt={name} />
        <h4>{name}</h4>
        <span>{role}</span>
      </div>
    );
  }
});

// Usage
<UserCard 
  name="Alice" 
  role="Developer" 
  avatar="/alice.png" 
/>`,
		playgroundTemplate: `const Greeting = component({
  name: 'Greeting',
  props: ['name', 'enthusiasm'],
  render() {
    const { name, enthusiasm = 1 } = this.props;
    const exclamation = '!'.repeat(enthusiasm);
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Hello, {name}{exclamation}</h2>
      </div>
    );
  }
});

const PropsDemo = component({
  name: 'PropsDemo',
  render() {
    return (
      <div style={{ padding: '2rem' }}>
        <Greeting name="LiteralJS" enthusiasm={3} />
        <Greeting name="World" enthusiasm={1} />
      </div>
    );
  }
});`,
		relatedMethods: ['component', 'render'],
		commonPatterns: [
			{
				name: 'Default Props',
				snippet: `const { title = 'Untitled', count = 0 } = this.props;`
			},
			{
				name: 'Callback Props',
				snippet: `<Child onUpdate={(val) => this.handleUpdate(val)} />`
			}
		]
	},
	{
		id: 'conditional',
		name: 'Conditional Rendering',
		category: 'core',
		tags: ['conditional', 'if', 'ternary', 'show', 'hide'],
		description: 'Conditionally render content using ternary operators, logical AND, or if statements in your render function.',
		syntax: `{isLoading ? <Spinner /> : <Content />}
{hasError && <ErrorMessage />}`,
		example: `component({
  name: 'StatusDisplay',
  state: { status: 'loading' },
  render() {
    const { status } = this.getState();
    return (
      <div>
        {status === 'loading' && <div>Loading...</div>}
        {status === 'error' && <div>Error occurred!</div>}
        {status === 'success' ? (
          <div>Data loaded!</div>
        ) : (
          <div>Waiting...</div>
        )}
      </div>
    );
  }
});`,
		playgroundTemplate: `const ConditionalDemo = component({
  name: 'ConditionalDemo',
  state: { showDetails: false, count: 0 },
  methods() {
    return {
      toggle() {
        this.setState({ showDetails: !this.getState().showDetails });
      },
      increment() {
        this.setState(s => ({ count: s.count + 1 }));
      }
    };
  },
  render() {
    const { showDetails, count } = this.getState();
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <button events={{ click: () => this.toggle() }}>
          {showDetails ? 'Hide' : 'Show'} Details
        </button>
        {showDetails && (
          <div style={{ 
            marginTop: '1rem',
            padding: '1rem',
            background: '#313244',
            borderRadius: '8px'
          }}>
            <p>These details are conditionally rendered!</p>
            <button events={{ click: () => this.increment() }}>
              Count: {count}
            </button>
          </div>
        )}
      </div>
    );
  }
});`,
		relatedMethods: ['render', 'getState'],
		commonPatterns: [
			{
				name: 'Loading States',
				snippet: `{isLoading ? <Spinner /> : <Content data={data} />}`
			},
			{
				name: 'Empty States',
				snippet: `{items.length === 0 ? <EmptyMessage /> : <List items={items} />}`
			}
		]
	},
	{
		id: 'lists',
		name: 'List Rendering',
		category: 'core',
		tags: ['list', 'array', 'map', 'filter', 'render'],
		description: 'Render arrays of data using map(), filter(), and other array methods inside your JSX.',
		syntax: `{items.map(item => (
  <li key={item.id}>{item.name}</li>
))}`,
		example: `component({
  name: 'UserList',
  state: { 
    users: [
      { id: 1, name: 'Alice', active: true },
      { id: 2, name: 'Bob', active: false }
    ]
  },
  render() {
    const { users } = this.getState();
    const activeUsers = users.filter(u => u.active);
    return (
      <div>
        <h3>Active Users ({activeUsers.length})</h3>
        <ul>
          {activeUsers.map(user => (
            <li key={user.id}>{user.name}</li>
          ))}
        </ul>
      </div>
    );
  }
});`,
		playgroundTemplate: `const ListDemo = component({
  name: 'ListDemo',
  state: {
    items: [
      { id: 1, text: 'Learn LiteralJS', done: true },
      { id: 2, text: 'Build an app', done: false },
      { id: 3, text: 'Deploy it', done: false }
    ]
  },
  methods() {
    return {
      toggle(id) {
        this.setState({
          items: this.getState().items.map(item =>
            item.id === id ? { ...item, done: !item.done } : item
          )
        });
      }
    };
  },
  render() {
    const { items } = this.getState();
    const doneCount = items.filter(i => i.done).length;
    return (
      <div style={{ padding: '2rem' }}>
        <h3>Tasks ({doneCount}/{items.length} done)</h3>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {items.map(item => (
            <li 
              key={item.id}
              style={{
                padding: '0.5rem',
                textDecoration: item.done ? 'line-through' : 'none',
                opacity: item.done ? 0.6 : 1,
                cursor: 'pointer'
              }}
              events={{ click: () => this.toggle(item.id) }}
            >
              {item.done ? '✅' : '⭕'} {item.text}
            </li>
          ))}
        </ul>
      </div>
    );
  }
});`,
		relatedMethods: ['render', 'h', 'autokeys'],
		commonPatterns: [
			{
				name: 'Filtered Lists',
				snippet: `{items
  .filter(item => item.active)
  .map(item => <Item key={item.id} data={item} />)
}`
			},
			{
				name: 'Sorted Lists',
				snippet: `{[...items]
  .sort((a, b) => a.name.localeCompare(b.name))
  .map(item => <Item key={item.id} data={item} />)
}`
			}
		]
	},
	{
		id: 'forms',
		name: 'Form Handling',
		category: 'core',
		tags: ['form', 'input', 'submit', 'validation', 'bind'],
		description: 'Handle form inputs, submissions, and validation with controlled components.',
		syntax: `<input 
  value={name} 
  events={{ input: (e) => this.setState({ name: e.target.value }) }} 
/>
<form events={{ submit: (e) => this.handleSubmit(e) }}>`,
		example: `component({
  name: 'ContactForm',
  state: { name: '', email: '', message: '' },
  methods() {
    return {
      updateField(field, value) {
        this.setState({ [field]: value });
      },
      submit(e) {
        e.preventDefault();
        const { name, email, message } = this.getState();
        console.log('Submit:', { name, email, message });
      }
    };
  },
  render() {
    const { name, email, message } = this.getState();
    return (
      <form events={{ submit: (e) => this.submit(e) }}>
        <input 
          value={name}
          placeholder="Name"
          events={{ input: (e) => this.updateField('name', e.target.value) }}
        />
        <input 
          value={email}
          placeholder="Email"
          events={{ input: (e) => this.updateField('email', e.target.value) }}
        />
        <button type="submit">Send</button>
      </form>
    );
  }
});`,
		playgroundTemplate: `const FormDemo = component({
  name: 'FormDemo',
  state: { name: '', color: '#cba6f7' },
  methods() {
    return {
      updateName(e) {
        this.setState({ name: e.target.value });
      },
      updateColor(e) {
        this.setState({ color: e.target.value });
      },
      submit(e) {
        e.preventDefault();
        alert(\`Hello, \${this.getState().name}!\`);
      }
    };
  },
  render() {
    const { name, color } = this.getState();
    return (
      <div style={{ padding: '2rem' }}>
        <form events={{ submit: (e) => this.submit(e) }}>
          <div style={{ marginBottom: '1rem' }}>
            <label>Name:</label>
            <input 
              value={name}
              events={{ input: (e) => this.updateName(e) }}
              style={{ marginLeft: '0.5rem' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label>Favorite Color:</label>
            <select 
              value={color}
              events={{ change: (e) => this.updateColor(e) }}
              style={{ marginLeft: '0.5rem' }}
            >
              <option value="#cba6f7">Purple</option>
              <option value="#89dceb">Cyan</option>
              <option value="#a6e3a1">Green</option>
              <option value="#f5c2e7">Pink</option>
            </select>
          </div>
          <button 
            type="submit"
            style={{
              padding: '0.75rem 1.5rem',
              background: color,
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Submit
          </button>
        </form>
        {name && (
          <p style={{ marginTop: '1rem' }}>
            Preview: Hello, <strong>{name}</strong>!
          </p>
        )}
      </div>
    );
  }
});`,
		relatedMethods: ['events', 'setState', 'getState'],
		commonPatterns: [
			{
				name: 'Two-Way Binding',
				snippet: `<input 
  value={text}
  events={{ input: (e) => this.setState({ text: e.target.value }) }}
/>`
			},
			{
				name: 'Form Validation',
				snippet: `submit(e) {
  e.preventDefault();
  const { email } = this.getState();
  if (!email.includes('@')) {
    this.setState({ error: 'Invalid email' });
    return;
  }
  // Submit...
}`
			}
		]
	},
	{
		id: 'refs',
		name: 'refs',
		category: 'core',
		tags: ['ref', 'reference', 'dom', 'focus', 'element'],
		description: 'Access DOM elements directly using refs. Useful for focusing inputs, measuring elements, or integrating with non-LiteralJS libraries.',
		syntax: `<input ref={el => this._input = el} />`,
		example: `component({
  name: 'FocusInput',
  state: { value: '' },
  methods() {
    return {
      focus() {
        if (this._input) {
          this._input.focus();
        }
      },
      updateValue(e) {
        this.setState({ value: e.target.value });
      }
    };
  },
  mounted() {
    this.focus();
  },
  render() {
    const { value } = this.getState();
    return (
      <div>
        <input 
          ref={el => this._input = el}
          value={value}
          events={{ input: (e) => this.updateValue(e) }}
        />
        <button events={{ click: () => this.focus() }}>
          Focus
        </button>
      </div>
    );
  }
});`,
		playgroundTemplate: `const RefsDemo = component({
  name: 'RefsDemo',
  state: { text: '' },
  methods() {
    return {
      updateText(e) {
        this.setState({ text: e.target.value });
      },
      focusInput() {
        if (this._input) this._input.focus();
      },
      clearInput() {
        this.setState({ text: '' });
        this.focusInput();
      }
    };
  },
  mounted() {
    this.focusInput();
  },
  render() {
    const { text } = this.getState();
    return (
      <div style={{ padding: '2rem' }}>
        <input
          ref={el => this._input = el}
          value={text}
          placeholder="Type something..."
          events={{ input: (e) => this.updateText(e) }}
          style={{ padding: '0.5rem', marginRight: '0.5rem' }}
        />
        <button events={{ click: () => this.focusInput() }}>
          Focus
        </button>
        <button events={{ click: () => this.clearInput() }}>
          Clear
        </button>
        <p style={{ marginTop: '1rem' }}>
          Length: {text.length} characters
        </p>
      </div>
    );
  }
});`,
		relatedMethods: ['mounted', 'unmounted'],
		commonPatterns: [
			{
				name: 'Focus Management',
				snippet: `mounted() {
  if (this._input) this._input.focus();
}`
			},
			{
				name: 'Third-Party Integration',
				snippet: `mounted() {
  if (this._chart) {
    new Chart(this._chart, { data: this.getState().data });
  }
}`
			}
		]
	},
	{
		id: 'memo',
		name: 'Memo',
		category: 'core',
		tags: ['memo', 'cache', 'performance', 'optimize'],
		description: 'Memoize expensive computations to avoid unnecessary recalculations on every render.',
		syntax: `const expensiveValue = memo(() => {
  return heavyComputation(this.getState().data);
}, [this.getState().data]);`,
		example: `component({
  name: 'ExpensiveList',
  state: { items: [], filter: '' },
  methods() {
    return {
      getFilteredItems() {
        const { items, filter } = this.getState();
        if (!filter) return items;
        return items.filter(item => 
          item.name.toLowerCase().includes(filter.toLowerCase())
        );
      }
    };
  },
  render() {
    const filtered = this.getFilteredItems();
    return (
      <div>
        <p>Showing {filtered.length} items</p>
        <ul>
          {filtered.map(item => <li key={item.id}>{item.name}</li>)}
        </ul>
      </div>
    );
  }
});`,
		playgroundTemplate: `const MemoDemo = component({
  name: 'MemoDemo',
  state: { count: 0, multiplier: 2 },
  methods() {
    return {
      computeExpensive() {
        const { count, multiplier } = this.getState();
        // Simulate expensive computation
        let result = 0;
        for (let i = 0; i < 1000000; i++) {
          result += count * multiplier;
        }
        return result;
      },
      increment() {
        this.setState(s => ({ count: s.count + 1 }));
      }
    };
  },
  render() {
    const { count, multiplier } = this.getState();
    const expensive = this.computeExpensive();
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h3>Count: {count}</h3>
        <p>Multiplier: {multiplier}</p>
        <p>Result: {expensive}</p>
        <button events={{ click: () => this.increment() }}>
          Increment
        </button>
      </div>
    );
  }
});`,
		relatedMethods: ['getState', 'render'],
		commonPatterns: [
			{
				name: 'Derived State',
				snippet: `getFullName() {
  const { firstName, lastName } = this.getState();
  return \`\${firstName} \${lastName}\`;
}`
			},
			{
				name: 'Filtered Data',
				snippet: `getVisibleTodos() {
  const { todos, filter } = this.getState();
  switch (filter) {
    case 'active': return todos.filter(t => !t.done);
    case 'completed': return todos.filter(t => t.done);
    default: return todos;
  }
}`
			}
		]
	},
	{
		id: 'scheduleupdate',
		name: 'scheduleUpdate()',
		category: 'lifecycle',
		tags: ['update', 'render', 'force', 'schedule'],
		description: 'Manually trigger a re-render of the component. Useful when integrating with external state or after async operations.',
		syntax: `this.scheduleUpdate();`,
		returns: { type: 'void', description: 'Triggers re-render asynchronously' },
		example: `component({
  name: 'AsyncData',
  state: { data: null },
  methods() {
    return {
      async loadData() {
        const response = await fetch('/api/data');
        const data = await response.json();
        this.setState({ data });
        // Force re-render if needed
        this.scheduleUpdate();
      }
    };
  },
  mounted() {
    this.loadData();
  },
  render() {
    const { data } = this.getState();
    return data ? <div>{data.title}</div> : <div>Loading...</div>;
  }
});`,
		playgroundTemplate: `const ScheduleDemo = component({
  name: 'ScheduleDemo',
  state: { clicks: 0 },
  methods() {
    return {
      handleClick() {
        this.setState({ clicks: this.getState().clicks + 1 });
        console.log('Clicks:', this.getState().clicks);
        // scheduleUpdate ensures render happens
        this.scheduleUpdate();
      }
    };
  },
  render() {
    const { clicks } = this.getState();
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h3>Manual Update Demo</h3>
        <p>Clicks: {clicks}</p>
        <button events={{ click: () => this.handleClick() }}>
          Click me
        </button>
      </div>
    );
  }
});`,
		relatedMethods: ['setState', 'mounted'],
		commonPatterns: [
			{
				name: 'External State Sync',
				snippet: `mounted() {
  this._unsubscribe = externalStore.subscribe(() => {
    this.setState({ data: externalStore.getData() });
    this.scheduleUpdate();
  });
}`
			},
			{
				name: 'Post-Async Update',
				snippet: `async saveData() {
  await api.save(this.getState().formData);
  this.setState({ saved: true });
  this.scheduleUpdate();
}`
			}
		]
	}
];

// Router-specific documentation
export const routerDocs = {
	overview: `The LiteralJS Router provides client-side navigation for single-page applications.

## Installation
\`\`\`bash
npm install literaljs-router
\`\`\`

## Basic Setup

1. Define your routes as an array of objects
2. Call \`Router({ routes })\` in your render
3. Use \`navigate(path)\` for programmatic navigation

## Route Matching

Routes are matched in order. The first matching route wins.

- Static routes: \`/about\`
- Named params: \`/user/:id\`
- Catch-all: Not supported (use explicit routes)`,

	routes: [
		{
			path: '/',
			name: 'Home',
			description: 'Landing page route',
			example: `{ path: '/', render: () => h(HomePage) }`
		},
		{
			path: '/about',
			name: 'About',
			description: 'Static about page',
			example: `{ path: '/about', render: () => h(AboutPage) }`
		},
		{
			path: '/user/:id',
			name: 'User Profile',
			description: 'Dynamic route with named parameter',
			example: `{ path: '/user/:id', render: ({ id }) => h(UserProfile, { userId: id }) }`
		}
	],

	navigation: `## Programmatic Navigation

Use \`navigate()\` to change routes:

\`\`\`javascript
import { navigate } from 'literaljs-router';

// In a component method
goToProfile() {
  navigate('/profile');
}

// With params
goToUser(userId) {
  navigate(\`/user/\${userId}\`);
}
\`\`\`

## Link Components

Create reusable link components:

\`\`\`javascript
const Link = component({
  name: 'Link',
  props: ['to', 'children'],
  methods() {
    return {
      handleClick(e) {
        e.preventDefault();
        navigate(this.props.to);
      }
    };
  },
  render() {
    return (
      <a href={this.props.to} events={{ click: (e) => this.handleClick(e) }}>
        {this.props.children}
      </a>
    );
  }
});
\`\`\``
};

// Todo app lesson data
export const todoLesson = {
	title: 'Build a Todo App',
	description: 'Learn LiteralJS by building a complete Todo application step by step!',
	steps: [
		{
			id: 1,
			title: 'Your First Component',
			description: 'Create a basic component that displays a title.',
			code: `const App = component({
  name: 'App',
  render() {
    return <h1>My Todo App</h1>;
  }
});`,
			task: 'Create a component that shows "My Todo App"',
			hint: 'Use component() with a name and render function',
			check: (code) => code.includes('My Todo App') && code.includes('component')
		},
		{
			id: 2,
			title: 'Adding State',
			description: 'Add state to track todos.',
			code: `const App = component({
  name: 'App',
  state: {
    todos: [
      { id: 1, text: 'Learn LiteralJS', done: false }
    ]
  },
  render() {
    const { todos } = this.getState();
    return (
      <div>
        <h1>My Todo App</h1>
        <ul>
          {todos.map(todo => (
            <li key={todo.id}>{todo.text}</li>
          ))}
        </ul>
      </div>
    );
  }
});`,
			task: 'Add state with a todos array and render it',
			hint: 'Use state: { todos: [...] } and this.getState()',
			check: (code) => code.includes('todos') && code.includes('getState')
		},
		{
			id: 3,
			title: 'Adding Methods',
			description: 'Add methods to interact with todos.',
			code: `const App = component({
  name: 'App',
  state: {
    todos: [
      { id: 1, text: 'Learn LiteralJS', done: false }
    ],
    newTodo: ''
  },
  methods() {
    return {
      addTodo() {
        const { todos, newTodo } = this.getState();
        if (!newTodo.trim()) return;
        this.setState({
          todos: [...todos, { id: Date.now(), text: newTodo, done: false }],
          newTodo: ''
        });
      },
      updateNewTodo(e) {
        this.setState({ newTodo: e.target.value });
      }
    };
  },
  render() {
    const { todos, newTodo } = this.getState();
    return (
      <div>
        <h1>My Todo App</h1>
        <div>
          <input
            value={newTodo}
            events={{ input: (e) => this.updateNewTodo(e) }}
          />
          <button events={{ click: () => this.addTodo() }}>
            Add
          </button>
        </div>
        <ul>
          {todos.map(todo => (
            <li key={todo.id}>{todo.text}</li>
          ))}
        </ul>
      </div>
    );
  }
});`,
			task: 'Add methods to create new todos',
			hint: 'Use methods() returning an object with functions',
			check: (code) => code.includes('methods') && code.includes('addTodo')
		},
		{
			id: 4,
			title: 'Toggle and Delete',
			description: 'Add functionality to mark todos complete and delete them.',
			code: `const App = component({
  name: 'App',
  state: {
    todos: [
      { id: 1, text: 'Learn LiteralJS', done: false },
      { id: 2, text: 'Build an app', done: false }
    ],
    newTodo: ''
  },
  methods() {
    return {
      addTodo() {
        const { todos, newTodo } = this.getState();
        if (!newTodo.trim()) return;
        this.setState({
          todos: [...todos, { id: Date.now(), text: newTodo, done: false }],
          newTodo: ''
        });
      },
      updateNewTodo(e) {
        this.setState({ newTodo: e.target.value });
      },
      toggleTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.map(todo =>
            todo.id === id ? { ...todo, done: !todo.done } : todo
          )
        });
      },
      deleteTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.filter(todo => todo.id !== id)
        });
      }
    };
  },
  render() {
    const { todos, newTodo } = this.getState();
    return (
      <div>
        <h1>My Todo App</h1>
        <div>
          <input
            value={newTodo}
            events={{ input: (e) => this.updateNewTodo(e) }}
          />
          <button events={{ click: () => this.addTodo() }}>
            Add
          </button>
        </div>
        <ul>
          {todos.map(todo => (
            <li key={todo.id}>
              <span
                style={{ textDecoration: todo.done ? 'line-through' : 'none' }}
                events={{ click: () => this.toggleTodo(todo.id) }}
              >
                {todo.done ? '✅' : '⭕'} {todo.text}
              </span>
              <button events={{ click: () => this.deleteTodo(todo.id) }}>
                🗑️
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }
});`,
			task: 'Add toggle and delete functionality',
			hint: 'Use map() to update and filter() to remove',
			check: (code) => code.includes('toggleTodo') && code.includes('deleteTodo')
		},
		{
			id: 5,
			title: 'Complete App',
			description: 'Add some polish with styling and a counter!',
			code: `const App = component({
  name: 'App',
  state: {
    todos: [
      { id: 1, text: 'Learn LiteralJS', done: true },
      { id: 2, text: 'Build a Todo App', done: false },
      { id: 3, text: 'Share with friends', done: false }
    ],
    newTodo: ''
  },
  methods() {
    return {
      addTodo() {
        const { todos, newTodo } = this.getState();
        if (!newTodo.trim()) return;
        this.setState({
          todos: [...todos, { id: Date.now(), text: newTodo, done: false }],
          newTodo: ''
        });
      },
      updateNewTodo(e) {
        this.setState({ newTodo: e.target.value });
      },
      toggleTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.map(todo =>
            todo.id === id ? { ...todo, done: !todo.done } : todo
          )
        });
      },
      deleteTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.filter(todo => todo.id !== id)
        });
      }
    };
  },
  render() {
    const { todos, newTodo } = this.getState();
    const doneCount = todos.filter(t => t.done).length;
    return (
      <div style={{ maxWidth: '400px', margin: '0 auto', padding: '2rem' }}>
        <h1>✨ My Todo App</h1>
        <p>{doneCount}/{todos.length} completed</p>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <input
            value={newTodo}
            style={{ flex: 1, padding: '0.5rem' }}
            events={{ input: (e) => this.updateNewTodo(e) }}
          />
          <button events={{ click: () => this.addTodo() }}>
            Add
          </button>
        </div>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {todos.map(todo => (
            <li key={todo.id} style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem',
              borderBottom: '1px solid #eee'
            }}>
              <span
                style={{ 
                  flex: 1,
                  textDecoration: todo.done ? 'line-through' : 'none',
                  opacity: todo.done ? 0.6 : 1,
                  cursor: 'pointer'
                }}
                events={{ click: () => this.toggleTodo(todo.id) }}
              >
                {todo.done ? '✅' : '⭕'} {todo.text}
              </span>
              <button events={{ click: () => this.deleteTodo(todo.id) }}>
                🗑️
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }
});`,
			task: 'Complete your todo app with styling',
			hint: 'Add inline styles and a completion counter',
			check: (code) => code.includes('doneCount') && code.includes('style')
		},
		{
			id: 6,
			title: 'Component Composition',
			description: 'Learn to nest components and pass data with props.',
			code: `const TodoItem = component({
  name: 'TodoItem',
  props: ['todo', 'onToggle', 'onDelete'],
  render() {
    const { todo, onToggle, onDelete } = this.props;
    return (
      <li style={{ 
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem',
        borderBottom: '1px solid #eee'
      }}>
        <span
          style={{ 
            flex: 1,
            textDecoration: todo.done ? 'line-through' : 'none',
            opacity: todo.done ? 0.6 : 1,
            cursor: 'pointer'
          }}
          events={{ click: () => onToggle(todo.id) }}
        >
          {todo.done ? '✅' : '⭕'} {todo.text}
        </span>
        <button events={{ click: () => onDelete(todo.id) }}>
          🗑️
        </button>
      </li>
    );
  }
});

const App = component({
  name: 'App',
  state: {
    todos: [
      { id: 1, text: 'Learn LiteralJS', done: true },
      { id: 2, text: 'Build a Todo App', done: false }
    ],
    newTodo: ''
  },
  methods() {
    return {
      addTodo() {
        const { todos, newTodo } = this.getState();
        if (!newTodo.trim()) return;
        this.setState({
          todos: [...todos, { id: Date.now(), text: newTodo, done: false }],
          newTodo: ''
        });
      },
      updateNewTodo(e) {
        this.setState({ newTodo: e.target.value });
      },
      toggleTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.map(todo =>
            todo.id === id ? { ...todo, done: !todo.done } : todo
          )
        });
      },
      deleteTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.filter(todo => todo.id !== id)
        });
      }
    };
  },
  render() {
    const { todos, newTodo } = this.getState();
    return (
      <div style={{ maxWidth: '400px', margin: '0 auto', padding: '2rem' }}>
        <h1>✨ My Todo App</h1>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <input
            value={newTodo}
            style={{ flex: 1, padding: '0.5rem' }}
            events={{ input: (e) => this.updateNewTodo(e) }}
          />
          <button events={{ click: () => this.addTodo() }}>
            Add
          </button>
        </div>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {todos.map(todo => (
            <TodoItem
              key={todo.id}
              todo={todo}
              onToggle={(id) => this.toggleTodo(id)}
              onDelete={(id) => this.deleteTodo(id)}
            />
          ))}
        </ul>
      </div>
    );
  }
});`,
			task: 'Extract TodoItem into its own component with props',
			hint: 'Use props array and this.props to receive data',
			check: (code) => code.includes('props') && code.includes('TodoItem') && code.includes('this.props')
		},
		{
			id: 7,
			title: 'Event Handling',
			description: 'Master different event types and event delegation patterns.',
			code: `const App = component({
  name: 'App',
  state: {
    todos: [],
    newTodo: '',
    filter: 'all'
  },
  methods() {
    return {
      addTodo(e) {
        e.preventDefault();
        const { todos, newTodo } = this.getState();
        if (!newTodo.trim()) return;
        this.setState({
          todos: [...todos, { id: Date.now(), text: newTodo, done: false }],
          newTodo: ''
        });
      },
      updateNewTodo(e) {
        this.setState({ newTodo: e.target.value });
      },
      toggleTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.map(todo =>
            todo.id === id ? { ...todo, done: !todo.done } : todo
          )
        });
      },
      deleteTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.filter(todo => todo.id !== id)
        });
      },
      setFilter(filter) {
        this.setState({ filter });
      },
      clearCompleted() {
        const { todos } = this.getState();
        this.setState({
          todos: todos.filter(todo => !todo.done)
        });
      }
    };
  },
  render() {
    const { todos, newTodo, filter } = this.getState();
    const filteredTodos = todos.filter(todo => {
      if (filter === 'active') return !todo.done;
      if (filter === 'completed') return todo.done;
      return true;
    });
    return (
      <div style={{ maxWidth: '400px', margin: '0 auto', padding: '2rem' }}>
        <h1>✨ My Todo App</h1>
        <form events={{ submit: (e) => this.addTodo(e) }}>
          <input
            value={newTodo}
            placeholder="What needs to be done?"
            style={{ flex: 1, padding: '0.5rem' }}
            events={{ input: (e) => this.updateNewTodo(e) }}
          />
          <button type="submit">Add</button>
        </form>
        <div style={{ display: 'flex', gap: '0.5rem', margin: '1rem 0' }}>
          <button events={{ click: () => this.setFilter('all') }}>All</button>
          <button events={{ click: () => this.setFilter('active') }}>Active</button>
          <button events={{ click: () => this.setFilter('completed') }}>Completed</button>
        </div>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {filteredTodos.map(todo => (
            <li key={todo.id}>
              <span
                style={{ textDecoration: todo.done ? 'line-through' : 'none' }}
                events={{ click: () => this.toggleTodo(todo.id) }}
              >
                {todo.done ? '✅' : '⭕'} {todo.text}
              </span>
              <button events={{ click: () => this.deleteTodo(todo.id) }}>🗑️</button>
            </li>
          ))}
        </ul>
        <button events={{ click: () => this.clearCompleted() }}>
          Clear Completed
        </button>
      </div>
    );
  }
});`,
			task: 'Add form submit, filter buttons, and clear completed events',
			hint: 'Use events={{ submit: ... }} on form, pass event object to handlers',
			check: (code) => code.includes('submit') && code.includes('filter') && code.includes('clearCompleted')
		},
		{
			id: 8,
			title: 'Conditional Rendering',
			description: 'Show and hide UI based on state with if/else, ternary, and &&.',
			code: `const App = component({
  name: 'App',
  state: {
    todos: [],
    newTodo: '',
    filter: 'all',
    showConfirm: false,
    todoToDelete: null
  },
  methods() {
    return {
      addTodo(e) {
        e.preventDefault();
        const { todos, newTodo } = this.getState();
        if (!newTodo.trim()) return;
        this.setState({
          todos: [...todos, { id: Date.now(), text: newTodo, done: false }],
          newTodo: ''
        });
      },
      updateNewTodo(e) {
        this.setState({ newTodo: e.target.value });
      },
      toggleTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.map(todo =>
            todo.id === id ? { ...todo, done: !todo.done } : todo
          )
        });
      },
      confirmDelete(id) {
        this.setState({ showConfirm: true, todoToDelete: id });
      },
      deleteTodo() {
        const { todos, todoToDelete } = this.getState();
        this.setState({
          todos: todos.filter(todo => todo.id !== todoToDelete),
          showConfirm: false,
          todoToDelete: null
        });
      },
      cancelDelete() {
        this.setState({ showConfirm: false, todoToDelete: null });
      }
    };
  },
  render() {
    const { todos, newTodo, showConfirm } = this.getState();
    const doneCount = todos.filter(t => t.done).length;
    const allDone = todos.length > 0 && doneCount === todos.length;

    return (
      <div style={{ maxWidth: '400px', margin: '0 auto', padding: '2rem' }}>
        <h1>✨ My Todo App</h1>

        {allDone && (
          <div style={{ background: '#a6e3a1', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
            🎉 All done! Great job!
          </div>
        )}

        <form events={{ submit: (e) => this.addTodo(e) }}>
          <input
            value={newTodo}
            placeholder={todos.length === 0 ? "Add your first todo!" : "What needs to be done?"}
            style={{ flex: 1, padding: '0.5rem' }}
            events={{ input: (e) => this.updateNewTodo(e) }}
          />
          <button type="submit">Add</button>
        </form>

        {todos.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#888', marginTop: '2rem' }}>
            No todos yet. Add one above!
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {todos.map(todo => (
              <li key={todo.id}>
                <span
                  style={{ textDecoration: todo.done ? 'line-through' : 'none' }}
                  events={{ click: () => this.toggleTodo(todo.id) }}
                >
                  {todo.done ? '✅' : '⭕'} {todo.text}
                </span>
                <button events={{ click: () => this.confirmDelete(todo.id) }}>🗑️</button>
              </li>
            ))}
          </ul>
        )}

        {showConfirm && (
          <div style={{ 
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center'
          }}>
            <div style={{ background: 'white', padding: '2rem', borderRadius: '12px' }}>
              <p>Are you sure you want to delete this todo?</p>
              <button events={{ click: () => this.deleteTodo() }}>Yes, Delete</button>
              <button events={{ click: () => this.cancelDelete() }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }
});`,
			task: 'Add conditional rendering: empty state, all-done banner, and confirm modal',
			hint: 'Use && for conditional blocks, ternary for if/else',
			check: (code) => code.includes('&&') && code.includes('?') && code.includes('showConfirm')
		},
		{
			id: 9,
			title: 'Lists and Keys',
			description: 'Render arrays efficiently and understand the importance of keys.',
			code: `const App = component({
  name: 'App',
  state: {
    todos: [
      { id: 1, text: 'Learn LiteralJS', done: true },
      { id: 2, text: 'Build a Todo App', done: false },
      { id: 3, text: 'Master keys', done: false }
    ],
    newTodo: '',
    nextId: 4
  },
  methods() {
    return {
      addTodo(e) {
        e.preventDefault();
        const { todos, newTodo, nextId } = this.getState();
        if (!newTodo.trim()) return;
        this.setState({
          todos: [...todos, { id: nextId, text: newTodo, done: false }],
          newTodo: '',
          nextId: nextId + 1
        });
      },
      updateNewTodo(e) {
        this.setState({ newTodo: e.target.value });
      },
      toggleTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.map(todo =>
            todo.id === id ? { ...todo, done: !todo.done } : todo
          )
        });
      },
      deleteTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.filter(todo => todo.id !== id)
        });
      },
      moveUp(index) {
        if (index === 0) return;
        const { todos } = this.getState();
        const newTodos = [...todos];
        [newTodos[index - 1], newTodos[index]] = [newTodos[index], newTodos[index - 1]];
        this.setState({ todos: newTodos });
      },
      moveDown(index) {
        const { todos } = this.getState();
        if (index === todos.length - 1) return;
        const newTodos = [...todos];
        [newTodos[index], newTodos[index + 1]] = [newTodos[index + 1], newTodos[index]];
        this.setState({ todos: newTodos });
      }
    };
  },
  render() {
    const { todos, newTodo } = this.getState();
    return (
      <div style={{ maxWidth: '400px', margin: '0 auto', padding: '2rem' }}>
        <h1>✨ My Todo App</h1>
        <form events={{ submit: (e) => this.addTodo(e) }}>
          <input
            value={newTodo}
            placeholder="What needs to be done?"
            style={{ flex: 1, padding: '0.5rem' }}
            events={{ input: (e) => this.updateNewTodo(e) }}
          />
          <button type="submit">Add</button>
        </form>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {todos.map((todo, index) => (
            <li key={todo.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem' }}>
              <span style={{ flex: 1, textDecoration: todo.done ? 'line-through' : 'none' }}
                events={{ click: () => this.toggleTodo(todo.id) }}>
                {todo.done ? '✅' : '⭕'} {todo.text}
              </span>
              <button events={{ click: () => this.moveUp(index) }}>⬆️</button>
              <button events={{ click: () => this.moveDown(index) }}>⬇️</button>
              <button events={{ click: () => this.deleteTodo(todo.id) }}>🗑️</button>
            </li>
          ))}
        </ul>
      </div>
    );
  }
});`,
			task: 'Add stable IDs and reordering buttons with moveUp/moveDown',
			hint: 'Use stable IDs (not index) as keys. Add moveUp/moveDown methods.',
			check: (code) => code.includes('moveUp') && code.includes('moveDown') && code.includes('key={todo.id}')
		},
		{
			id: 10,
			title: 'Form Handling',
			description: 'Build robust forms with input binding, validation, and submission.',
			code: `const App = component({
  name: 'App',
  state: {
    todos: [],
    newTodo: '',
    error: '',
    priority: 'medium',
    category: 'personal'
  },
  methods() {
    return {
      validateTodo(text) {
        if (!text.trim()) return 'Todo text is required';
        if (text.length < 3) return 'Todo must be at least 3 characters';
        if (text.length > 100) return 'Todo must be less than 100 characters';
        return '';
      },
      addTodo(e) {
        e.preventDefault();
        const { todos, newTodo, priority, category } = this.getState();
        const error = this.validateTodo(newTodo);
        if (error) {
          this.setState({ error });
          return;
        }
        this.setState({
          todos: [...todos, { 
            id: Date.now(), 
            text: newTodo.trim(), 
            done: false,
            priority,
            category
          }],
          newTodo: '',
          error: '',
          priority: 'medium',
          category: 'personal'
        });
      },
      updateNewTodo(e) {
        this.setState({ newTodo: e.target.value, error: '' });
      },
      updatePriority(e) {
        this.setState({ priority: e.target.value });
      },
      updateCategory(e) {
        this.setState({ category: e.target.value });
      },
      toggleTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.map(todo =>
            todo.id === id ? { ...todo, done: !todo.done } : todo
          )
        });
      },
      deleteTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.filter(todo => todo.id !== id)
        });
      }
    };
  },
  render() {
    const { todos, newTodo, error, priority, category } = this.getState();
    return (
      <div style={{ maxWidth: '400px', margin: '0 auto', padding: '2rem' }}>
        <h1>✨ My Todo App</h1>
        <form events={{ submit: (e) => this.addTodo(e) }}>
          <div>
            <input
              value={newTodo}
              placeholder="What needs to be done?"
              style={{ 
                flex: 1, 
                padding: '0.5rem',
                border: error ? '2px solid #f38ba8' : '1px solid #ccc'
              }}
              events={{ input: (e) => this.updateNewTodo(e) }}
            />
            {error && <span style={{ color: '#f38ba8', fontSize: '0.875rem' }}>{error}</span>}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', margin: '0.5rem 0' }}>
            <select value={priority} events={{ change: (e) => this.updatePriority(e) }}>
              <option value="low">Low Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="high">High Priority</option>
            </select>
            <select value={category} events={{ change: (e) => this.updateCategory(e) }}>
              <option value="personal">Personal</option>
              <option value="work">Work</option>
              <option value="shopping">Shopping</option>
            </select>
          </div>
          <button type="submit">Add Todo</button>
        </form>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {todos.map(todo => (
            <li key={todo.id} style={{ 
              padding: '0.5rem',
              borderLeft: todo.priority === 'high' ? '4px solid #f38ba8' : 
                         todo.priority === 'medium' ? '4px solid #fab387' : '4px solid #a6e3a1'
            }}>
              <span style={{ textDecoration: todo.done ? 'line-through' : 'none' }}
                events={{ click: () => this.toggleTodo(todo.id) }}>
                {todo.done ? '✅' : '⭕'} {todo.text}
              </span>
              <span style={{ fontSize: '0.75rem', color: '#888' }}>
                {todo.category} • {todo.priority}
              </span>
              <button events={{ click: () => this.deleteTodo(todo.id) }}>🗑️</button>
            </li>
          ))}
        </ul>
      </div>
    );
  }
});`,
			task: 'Add form validation, select inputs, and priority/category fields',
			hint: 'Create a validateTodo method, use select elements with events={{ change: ... }}',
			check: (code) => code.includes('validateTodo') && code.includes('error') && code.includes('select')
		},
		{
			id: 11,
			title: 'Lifecycle Deep Dive',
			description: 'Use mounted and unmounted for setup, timers, and cleanup.',
			code: `const App = component({
  name: 'App',
  state: {
    todos: [],
    newTodo: '',
    currentTime: new Date().toLocaleTimeString(),
    saved: false
  },
  methods() {
    return {
      addTodo(e) {
        e.preventDefault();
        const { todos, newTodo } = this.getState();
        if (!newTodo.trim()) return;
        this.setState({
          todos: [...todos, { id: Date.now(), text: newTodo, done: false }],
          newTodo: ''
        });
      },
      updateNewTodo(e) {
        this.setState({ newTodo: e.target.value });
      },
      toggleTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.map(todo =>
            todo.id === id ? { ...todo, done: !todo.done } : todo
          )
        });
      },
      deleteTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.filter(todo => todo.id !== id)
        });
      },
      updateTime() {
        this.setState({ currentTime: new Date().toLocaleTimeString() });
      },
      saveTodos() {
        const { todos } = this.getState();
        localStorage.setItem('my-todos', JSON.stringify(todos));
        this.setState({ saved: true });
        setTimeout(() => this.setState({ saved: false }), 2000);
      }
    };
  },
  mounted() {
    // Load saved todos
    const saved = localStorage.getItem('my-todos');
    if (saved) {
      this.setState({ todos: JSON.parse(saved) });
    }
    // Start clock
    this._timer = setInterval(() => this.updateTime(), 1000);
    // Auto-save every 30 seconds
    this._autoSave = setInterval(() => this.saveTodos(), 30000);
  },
  unmounted() {
    // Clean up all timers
    clearInterval(this._timer);
    clearInterval(this._autoSave);
    // Save one last time
    const { todos } = this.getState();
    localStorage.setItem('my-todos', JSON.stringify(todos));
  },
  render() {
    const { todos, newTodo, currentTime, saved } = this.getState();
    return (
      <div style={{ maxWidth: '400px', margin: '0 auto', padding: '2rem' }}>
        <h1>✨ My Todo App</h1>
        <p>🕐 {currentTime}</p>
        {saved && <p style={{ color: '#a6e3a1' }}>💾 Saved!</p>}
        <form events={{ submit: (e) => this.addTodo(e) }}>
          <input
            value={newTodo}
            placeholder="What needs to be done?"
            style={{ flex: 1, padding: '0.5rem' }}
            events={{ input: (e) => this.updateNewTodo(e) }}
          />
          <button type="submit">Add</button>
        </form>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {todos.map(todo => (
            <li key={todo.id}>
              <span style={{ textDecoration: todo.done ? 'line-through' : 'none' }}
                events={{ click: () => this.toggleTodo(todo.id) }}>
                {todo.done ? '✅' : '⭕'} {todo.text}
              </span>
              <button events={{ click: () => this.deleteTodo(todo.id) }}>🗑️</button>
            </li>
          ))}
        </ul>
        <button events={{ click: () => this.saveTodos() }}>
          💾 Save Now
        </button>
      </div>
    );
  }
});`,
			task: 'Add mounted/unmounted hooks with timer, localStorage load/save',
			hint: 'Use mounted() to load data and start timers, unmounted() to clean up',
			check: (code) => code.includes('mounted()') && code.includes('unmounted()') && code.includes('localStorage')
		},
		{
			id: 12,
			title: 'App Store',
			description: 'Share state between components using the app store.',
			code: `const TodoList = component({
  name: 'TodoList',
  render() {
    const { todos } = this.getStore();
    return (
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {todos.map(todo => (
          <li key={todo.id}>
            <span>{todo.done ? '✅' : '⭕'} {todo.text}</span>
          </li>
        ))}
      </ul>
    );
  }
});

const TodoStats = component({
  name: 'TodoStats',
  render() {
    const { todos, filter } = this.getStore();
    const doneCount = todos.filter(t => t.done).length;
    const total = todos.length;
    return (
      <div style={{ padding: '1rem', background: '#313244', borderRadius: '8px' }}>
        <p>{doneCount}/{total} completed</p>
        <p>Filter: {filter}</p>
      </div>
    );
  }
});

const App = component({
  name: 'App',
  state: {
    newTodo: ''
  },
  methods() {
    return {
      addTodo(e) {
        e.preventDefault();
        const { newTodo } = this.getState();
        const { todos } = this.getStore();
        if (!newTodo.trim()) return;
        this.setStore({
          todos: [...todos, { id: Date.now(), text: newTodo, done: false }]
        });
        this.setState({ newTodo: '' });
      },
      updateNewTodo(e) {
        this.setState({ newTodo: e.target.value });
      },
      setFilter(filter) {
        this.setStore({ filter });
      }
    };
  },
  render() {
    const { newTodo } = this.getState();
    return (
      <div style={{ maxWidth: '400px', margin: '0 auto', padding: '2rem' }}>
        <h1>✨ My Todo App</h1>
        <TodoStats />
        <form events={{ submit: (e) => this.addTodo(e) }}>
          <input
            value={newTodo}
            placeholder="What needs to be done?"
            style={{ flex: 1, padding: '0.5rem' }}
            events={{ input: (e) => this.updateNewTodo(e) }}
          />
          <button type="submit">Add</button>
        </form>
        <div style={{ display: 'flex', gap: '0.5rem', margin: '1rem 0' }}>
          <button events={{ click: () => this.setFilter('all') }}>All</button>
          <button events={{ click: () => this.setFilter('active') }}>Active</button>
          <button events={{ click: () => this.setFilter('completed') }}>Completed</button>
        </div>
        <TodoList />
      </div>
    );
  }
});`,
			task: 'Use getStore() and setStore() to share todos between TodoList and TodoStats',
			hint: 'Create child components that read from this.getStore(), update with this.setStore()',
			check: (code) => code.includes('getStore') && code.includes('setStore') && code.includes('TodoList') && code.includes('TodoStats')
		},
		{
			id: 13,
			title: 'Router Setup',
			description: 'Add client-side routing to navigate between views.',
			code: `import { Router, navigate } from 'literaljs-router';

const Home = component({
  name: 'Home',
  render() {
    return (
      <div>
        <h2>🏠 Home</h2>
        <p>Welcome to your Todo App!</p>
        <button events={{ click: () => navigate('/todos') }}>
          Go to Todos
        </button>
      </div>
    );
  }
});

const Todos = component({
  name: 'Todos',
  state: {
    todos: [
      { id: 1, text: 'Learn LiteralJS', done: true },
      { id: 2, text: 'Add routing', done: false }
    ],
    newTodo: ''
  },
  methods() {
    return {
      addTodo(e) {
        e.preventDefault();
        const { todos, newTodo } = this.getState();
        if (!newTodo.trim()) return;
        this.setState({
          todos: [...todos, { id: Date.now(), text: newTodo, done: false }],
          newTodo: ''
        });
      },
      updateNewTodo(e) {
        this.setState({ newTodo: e.target.value });
      },
      toggleTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.map(todo =>
            todo.id === id ? { ...todo, done: !todo.done } : todo
          )
        });
      },
      deleteTodo(id) {
        const { todos } = this.getState();
        this.setState({
          todos: todos.filter(todo => todo.id !== id)
        });
      }
    };
  },
  render() {
    const { todos, newTodo } = this.getState();
    return (
      <div>
        <h2>📝 Todos</h2>
        <button events={{ click: () => navigate('/') }}>← Back Home</button>
        <form events={{ submit: (e) => this.addTodo(e) }}>
          <input
            value={newTodo}
            placeholder="What needs to be done?"
            events={{ input: (e) => this.updateNewTodo(e) }}
          />
          <button type="submit">Add</button>
        </form>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {todos.map(todo => (
            <li key={todo.id}>
              <span style={{ textDecoration: todo.done ? 'line-through' : 'none' }}
                events={{ click: () => this.toggleTodo(todo.id) }}>
                {todo.done ? '✅' : '⭕'} {todo.text}
              </span>
              <button events={{ click: () => this.deleteTodo(todo.id) }}>🗑️</button>
            </li>
          ))}
        </ul>
      </div>
    );
  }
});

const About = component({
  name: 'About',
  render() {
    return (
      <div>
        <h2>ℹ️ About</h2>
        <p>This todo app was built with LiteralJS!</p>
        <button events={{ click: () => navigate('/') }}>← Back Home</button>
      </div>
    );
  }
});

const routes = [
  { path: '/', render: () => h(Home) },
  { path: '/todos', render: () => h(Todos) },
  { path: '/about', render: () => h(About) }
];

const App = component({
  name: 'App',
  render() {
    return (
      <div style={{ maxWidth: '400px', margin: '0 auto', padding: '2rem' }}>
        <nav style={{ marginBottom: '1rem' }}>
          <button events={{ click: () => navigate('/') }}>Home</button>
          <button events={{ click: () => navigate('/todos') }}>Todos</button>
          <button events={{ click: () => navigate('/about') }}>About</button>
        </nav>
        <main>{Router({ routes })}</main>
      </div>
    );
  }
});`,
			task: 'Set up routes with Home, Todos, and About pages using literaljs-router',
			hint: 'Import Router and navigate, define routes array, render Router in your app',
			check: (code) => code.includes('Router') && code.includes('navigate') && code.includes('routes')
		},
		{
			id: 14,
			title: 'Deployment',
			description: 'Build and deploy your LiteralJS application.',
			code: `// Build your app for production
// 1. Install dependencies
// npm install

// 2. Build for production
// npm run build

// 3. The dist/ folder contains your static files
// - index.html
// - bundle.js
// - styles.css

// Deploy to Netlify
// - Drag and drop the dist/ folder to Netlify
// - Or use the Netlify CLI: netlify deploy --prod --dir=dist

// Deploy to Vercel
// - Install Vercel CLI: npm i -g vercel
// - Run: vercel --prod

// Deploy to GitHub Pages
// - Use gh-pages package: npm install --save-dev gh-pages
// - Add to package.json scripts:
//   "deploy": "gh-pages -d dist"
// - Run: npm run deploy

const App = component({
  name: 'App',
  state: {
    todos: [
      { id: 1, text: 'Learn LiteralJS', done: true },
      { id: 2, text: 'Build a Todo App', done: true },
      { id: 3, text: 'Deploy to production', done: true }
    ]
  },
  render() {
    const { todos } = this.getState();
    return (
      <div style={{ maxWidth: '400px', margin: '0 auto', padding: '2rem', textAlign: 'center' }}>
        <h1>🚀 Deployed!</h1>
        <p>Your LiteralJS app is live!</p>
        <div style={{ fontSize: '4rem', margin: '2rem 0' }}>
          🎉
        </div>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {todos.map(todo => (
            <li key={todo.id} style={{ padding: '0.5rem' }}>
              {todo.done ? '✅' : '⭕'} {todo.text}
            </li>
          ))}
        </ul>
        <p style={{ color: '#888', marginTop: '2rem' }}>
          Built with ❤️ using LiteralJS
        </p>
      </div>
    );
  }
});`,
			task: 'Write deployment commands for Netlify, Vercel, and GitHub Pages',
			hint: 'Include npm run build, and deployment commands for at least one platform',
			check: (code) => code.includes('build') && (code.includes('netlify') || code.includes('vercel') || code.includes('gh-pages'))
		}
	]
};
